import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useYupValidation } from '@future-harry/nxt-styles';
import { useSettingsStore } from '@/apps/admin/stores/settings.store';
import { SettingsTabId } from '@/apps/admin/components/settings/constants';
import { notifyError } from '@/core/helpers/notification.helper';
import type { JobBoard } from '@/core/collections/job-boards/job-board.interface';
import * as JobBoardSchema from '../components/forms/settings/job-board-settings.schema';

export function useJobBoardSettings(jobBoardRef: Ref<JobBoard>) {
   const { t } = useI18n();
   const settingsStore = useSettingsStore();

   const isSettingsOpen = ref(false);
   const openSettings = () => (isSettingsOpen.value = true);
   const closeSettings = () => {
      settingsStore.setActiveTab(SettingsTabId.General);
      isSettingsOpen.value = false;
   };

   const { errors, validate: validateYup } = useYupValidation({
      schema: JobBoardSchema.getAllRules(),
      data: jobBoardRef,
   });

   settingsStore.setValidationErrors(errors);

   const validate = (): boolean => {
      const result = validateYup();

      if (result.isValid) {
         settingsStore.resetValidationErrors();
         return true;
      }

      const errorKeys = Object.keys(result.errors);

      // Theme tab errors
      if (errorKeys.some((key) => ['border_radius'].includes(key))) {
         settingsStore.setActiveTab(SettingsTabId.Theme);
      }

      // Footer tab errors
      if (
         errorKeys.some((key) =>
            ['contact_email', 'social_urls.facebook', 'social_urls.x', 'social_urls.linkedin', 'social_urls.instagram'].includes(key),
         )
      ) {
         settingsStore.setActiveTab(SettingsTabId.Footer);
      }

      // Header tab errors
      if (errorKeys.some((key) => ['language_ids', 'home_url'].includes(key))) {
         settingsStore.setActiveTab(SettingsTabId.Header);
      }

      // General tab errors
      if (errorKeys.some((key) => ['name', 'url'].includes(key))) {
         settingsStore.setActiveTab(SettingsTabId.General);
      }

      notifyError({ title: t('job-board.settings.fix-errors') });
      return false;
   };

   return { isSettingsOpen, openSettings, closeSettings, errors, validate };
}
